import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Film, Mail, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('Reset instructions sent!');
      // In production, this would be emailed. For now, show token
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative flex items-center justify-center px-4">
      <div className="hero-glow absolute top-0 left-0 right-0 h-[500px]" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Film className="w-10 h-10 text-[#007AFF] mr-3" />
            <h1 className="heading-font text-4xl font-bold uppercase tracking-tight text-white">
              TRANSCRIBE HUB
            </h1>
          </div>
          <p className="text-[#A1A1AA]">Reset your password</p>
          <p className="text-[#52525B] text-sm mt-2">Enter your email to get a reset link</p>
        </div>

        <div className="glass-card rounded-xl p-8">
          {!resetToken ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                    placeholder="your@email.com"
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Reset Link...
                  </>
                ) : (
                  'Get Reset Link'
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-white mb-4 font-bold">Password Reset Link Generated!</p>
              <p className="text-[#A1A1AA] text-sm mb-4">Copy this link and open it in your browser:</p>
              <div className="bg-[#1A1A1A] p-4 rounded-md border border-[#27272A]">
                <code className="text-[#34C759] text-xs break-all">
                  {window.location.origin}/reset-password?token={resetToken}
                </code>
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/reset-password?token=${resetToken}`);
                  toast.success('Link copied to clipboard!');
                }}
                className="w-full btn-secondary"
              >
                Copy Link
              </Button>
              <Link to={`/reset-password?token=${resetToken}`}>
                <Button className="w-full btn-primary">
                  Reset Password Now
                </Button>
              </Link>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-[#007AFF] hover:underline text-sm flex items-center justify-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
        
        {/* Branding */}
        <div className="text-center mt-6">
          <p className="text-[#52525B] text-xs">Enabled by Bingelabs</p>
        </div>
      </div>
    </div>
  );
}
